'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { NavigationCategory, NavigationEntry } from '@/types/navigation';

// Trailing-slash-insensitive equality, so "/konzept" and "/konzept/" both
// count as the current page.
const isSamePath = (a?: string | null, b?: string | null) => {
  if (!a || !b) return false;
  const norm = (s: string) => (s.length > 1 ? s.replace(/\/$/, '') : s);
  return norm(a) === norm(b);
};

export type NavStyle = 'chips' | 'split' | 'classic';

interface NavigationProps {
  categories: NavigationCategory[];
  // Each independent of the others - e.g. category icons with no color, or
  // color with no icons. All Design System toggles.
  categoryIcons?: boolean;
  categoryPlayful?: boolean; // colorful pill/segmented shape + bg/text color
  entryIcons?: boolean;
  entryPlayful?: boolean; // dropdown entries' own bg/text color
  // "chips": individually-rounded pill buttons (default). "split": a single
  // seamless segmented group (only the two end buttons rounded) - saves
  // horizontal space. Both only have a visible effect when categoryPlayful is
  // on. "classic": the original site's look modernized - rectangular uppercase
  // segments, first 4 green / rest gray (hardcoded), no dropdown arrows, no
  // current-page checkmarks - active regardless of categoryPlayful.
  navStyle?: NavStyle;
  // Only meaningful with navStyle "classic": categories' own Strapi Bg/Text
  // colors replace the hardcoded green/gray.
  classicColorOverride?: boolean;
}

interface NavigationItemProps {
  category: NavigationCategory;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  isMobileMenu: boolean;
  categoryIcons?: boolean;
  categoryPlayful?: boolean;
  entryIcons?: boolean;
  entryPlayful?: boolean;
  navStyle?: NavStyle;
  classicColorOverride?: boolean;
  colorIndex: number;
  isFirst: boolean;
  isLast: boolean;
  pathname: string | null;
}

// "classic" nav hardcoded palette: first 4 categories green, the rest gray -
// white uppercase text on both. Green = the site accent (green-700).
const CLASSIC_GREEN = { backgroundColor: '#15803d', color: '#ffffff' };
const CLASSIC_GRAY = { backgroundColor: '#6b7280', color: '#ffffff' };

// Cycled by category order when a category has no bgColor/textColor of its
// own set in Strapi - a Grundschule-playground palette (matches the nav
// icons' own colors), not an arbitrary rainbow.
const PLAYFUL_PALETTE = [
  { bg: '#e2f2fb', bgDark: '#1c3a52', text: '#2f6fb0', textDark: '#8bc2ef' },
  { bg: '#fdf3d8', bgDark: '#4a3c14', text: '#8a6a10', textDark: '#f2c94c' },
  { bg: '#fbe3e3', bgDark: '#4a2020', text: '#b53939', textDark: '#f2a3a3' },
  { bg: '#e6f2df', bgDark: '#25401a', text: '#4f7a1f', textDark: '#a3d476' },
];

// Plain <img>, not next/image: icons are small, decorative, and may be SVG
// (Next's image optimizer rejects SVG sources unless dangerouslyAllowSVG is
// set, which this project doesn't set) - no optimization needed here anyway.
const Icon: React.FC<{ url?: string | null; className?: string }> = ({ url, className }) =>
  url ? <img src={url} alt="" aria-hidden="true" width={20} height={20} className={className} /> : null;

const NavigationItem: React.FC<NavigationItemProps> = ({
  category, isOpen, onToggle, onClose, isMobileMenu,
  categoryIcons, categoryPlayful, entryIcons, entryPlayful, navStyle, classicColorOverride, colorIndex, isFirst, isLast, pathname,
}) => {
  const sortedEntries = category.navigation_entries?.sort((a, b) => a.order - b.order) || [];
  const [hovered, setHovered] = useState(false);
  // Breadcrumb-style "you are here" - true when one of this category's own
  // entries is the current page, regardless of hover/open state.
  const categoryHasActiveEntry = sortedEntries.some((e) => isSamePath(e.link, pathname));

  // Desktop: show on hover OR click/tap toggle. Mobile menu: only click toggle.
  const showDropdown = isMobileMenu ? isOpen : (hovered || isOpen);

  const fallback = PLAYFUL_PALETTE[colorIndex % PLAYFUL_PALETTE.length];
  const isClassicNav = navStyle === 'classic';
  const isSplit = !isClassicNav && categoryPlayful && navStyle === 'split';
  // Classic: hardcoded green/gray by position - unless the dependent override
  // flag says to honor the category's own Strapi colors instead.
  const classicStyle: React.CSSProperties =
    classicColorOverride && (category.bgColor || category.textColor)
      ? { backgroundColor: category.bgColor || undefined, color: category.textColor || undefined }
      : colorIndex < 4 ? CLASSIC_GREEN : CLASSIC_GRAY;
  const buttonStyle: React.CSSProperties | undefined = isClassicNav
    ? classicStyle
    : categoryPlayful
      ? { backgroundColor: category.bgColor || fallback.bg, color: category.textColor || fallback.text }
      : undefined;
  // On mobile, playful categories drop the floating-pill look for a flat,
  // full-width list row - matching the entry list items directly below them
  // (same flat color fill, no radius/border) - instead of stacked chips.
  const isMobilePlayfulList = categoryPlayful && !isClassicNav && isMobileMenu;
  // Plain (non-playful, non-classic) desktop buttons need to be dense enough
  // that all 7 categories fit on one row - the mobile list keeps the
  // original roomier touch target since it stacks vertically instead.
  const isMobilePlainList = !categoryPlayful && !isClassicNav && isMobileMenu;
  const isDesktopPlain = !categoryPlayful && !isClassicNav && !isMobileMenu;
  const buttonClass = isClassicNav
    ? 'nav-classic-btn w-full h-full flex items-center justify-center gap-2 p-1 uppercase font-semibold tracking-wide text-sm rounded-none transition-[filter] duration-150 hover:brightness-110 touch-manipulation'
    : isMobilePlayfulList
    ? 'w-full flex items-center gap-2 px-4 py-3 font-bold transition-colors duration-200 touch-manipulation'
    : isMobilePlainList
    ? 'flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-50 bg-background dark:hover:bg-gray-800 rounded-md transition-colors duration-200 touch-manipulation'
    : categoryPlayful
    ? `flex items-center gap-1.5 px-3 py-1.5 text-sm font-bold transition-all duration-200 touch-manipulation border-2 ${
        isSplit
          // Small radius (not a full pill) - every button keeps its own full
          // border so a wrapped second row never renders with a missing/open
          // left edge; adjacent buttons in the same row instead overlap by
          // 2px (-ml) so the shared border reads as one seamless line.
          ? `rounded-[4px] ${isFirst ? '' : '-ml-[2px]'}`
          : 'rounded-full hover:scale-105'
      }`
    : 'flex items-center gap-2 px-3 py-2 text-base text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-50 bg-background dark:hover:bg-gray-800 rounded-md transition-colors duration-200 touch-manipulation';
  const borderColor = !isClassicNav && categoryPlayful && !isMobilePlayfulList ? (category.textColor || fallback.text) : undefined;

  const icon = categoryIcons ? (
    <Icon url={category.icon?.url} className={`nav-category-icon shrink-0 ${
      categoryPlayful && !isMobilePlayfulList ? 'w-4 h-4' : 'w-5 h-5'
    }`} />
  ) : null;

  if (sortedEntries.length === 0) {
    return (
      <div className={isClassicNav && !isMobileMenu ? 'flex-1 flex' : undefined}>
        <button type="button" className={buttonClass} style={buttonStyle ? { ...buttonStyle, borderColor } : undefined}>
          {icon}
          <span className={categoryPlayful || isClassicNav ? undefined : 'font-bold'}>{category.name}</span>
        </button>
      </div>
    );
  }

  return (
    <div
      className={isClassicNav && !isMobileMenu ? 'relative flex-1 flex' : 'relative'}
      onMouseEnter={() => { if (!isMobileMenu) setHovered(true); }}
      onMouseLeave={() => { if (!isMobileMenu) { setHovered(false); } }}
    >
      <button
        type="button"
        onClick={onToggle}
        onTouchEnd={(e) => {
          // Prevent the ghost click + mouseenter on touch devices (iPad).
          // This ensures tap toggles the dropdown reliably.
          e.preventDefault();
          onToggle();
        }}
        onKeyDown={(e) => {
          // Belt-and-suspenders: guarantee Enter/Space toggle the menu even if
          // something upstream (custom CSS, an ancestor handler) interferes with
          // the browser's default "activate focused button" behavior.
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
            e.preventDefault();
            onToggle();
          }
        }}
        className={buttonClass}
        style={buttonStyle ? { ...buttonStyle, borderColor } : undefined}
        aria-haspopup="true"
        aria-expanded={showDropdown}
      >
        {icon}
        <span className={categoryPlayful || isClassicNav ? undefined : 'font-bold'}>{category.name}</span>
        {/* Classic style: no dropdown arrow and no breadcrumb checkmark. */}
        {!isClassicNav && (
          <span className={`flex items-center gap-1 shrink-0 ${isMobileMenu || isDesktopPlain ? 'ml-2' : 'ml-1'}`}>
            {/* "You are here" - static, independent of hover/open state. */}
            {categoryHasActiveEntry && (
              <svg className={`nav-current-check shrink-0 ${isDesktopPlain ? 'h-5 w-5' : 'h-4 w-4'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            )}
            {/* Dropdown open/close indicator - always shown alongside the checkmark. */}
            <svg
              className={`transform transition-transform duration-200 ${isDesktopPlain ? 'h-5 w-5' : 'h-4 w-4'} ${
                showDropdown ? 'rotate-180' : 'rotate-0'
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </span>
        )}
      </button>

      {showDropdown && (
        <div className={`${isMobileMenu ? 'relative w-full' : 'absolute top-full left-0 w-64'} bg-background shadow-lg rounded-md border border-gray-200 dark:border-gray-700 z-50 flex flex-col max-h-96 overflow-y-auto`}>
          {sortedEntries.map((entry) => {
            const entryStyle: React.CSSProperties | undefined = entryPlayful && (entry.bgColor || entry.textColor)
              ? { backgroundColor: entry.bgColor || undefined, color: entry.textColor || undefined }
              : undefined;
            const isCurrentEntry = isSamePath(entry.link, pathname);
            return (
              <Link
                key={entry.id}
                href={entry.link}
                className={`nav-entry-link flex items-center gap-2 px-4 py-3 text-sm transition-colors duration-200 touch-manipulation hover:font-bold ${
                  entryStyle ? '' : 'text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
                style={entryStyle}
                onClick={onClose}
              >
                {entryIcons && <Icon url={entry.icon?.url} className="nav-entry-icon w-5 h-5 shrink-0" />}
                <span className="flex-1">{entry.label}</span>
                {isCurrentEntry && !isClassicNav && (
                  <svg className="nav-current-check h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const Navigation: React.FC<NavigationProps> = ({
  categories, categoryIcons, categoryPlayful, entryIcons, entryPlayful, navStyle, classicColorOverride,
}) => {
  const [openCategory, setOpenCategory] = useState<number | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const pathname = usePathname();
  const sortedCategories = categories.sort((a, b) => a.order - b.order);
  const isClassicNav = navStyle === 'classic';
  const isSplit = !isClassicNav && categoryPlayful && navStyle === 'split';

  const handleToggle = useCallback((categoryId: number) => {
    setOpenCategory(prev => prev === categoryId ? null : categoryId);
  }, []);

  const handleClose = useCallback(() => {
    setOpenCategory(null);
    setMobileMenuOpen(false);
  }, []);

  // Close menu when clicking/tapping outside
  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setOpenCategory(null);
        setMobileMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenCategory(null);
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  return (
    <nav ref={navRef} className="site-nav relative flex justify-end sm:block">
      {/* Hamburger button for mobile (right-aligned) */}
      <button
        type="button"
        className="sm:hidden flex items-center px-2 py-2 text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 touch-manipulation"
        onClick={() => setMobileMenuOpen(prev => !prev)}
        aria-label="Toggle menu"
      >
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {mobileMenuOpen ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          )}
        </svg>
      </button>

      {/* Desktop horizontal nav */}
      {isClassicNav ? (
        // Classic: the nav mirrors the 4:3 banner split above it - the green
        // group spans the same 4 units as the first banner image, the gray
        // group the 3 units of the second, so the gray group's left edge
        // aligns exactly with the second header picture. Buttons stretch
        // evenly within their group.
        <div className="hidden sm:flex w-full items-stretch gap-[3px] bg-background">
          <div className="flex flex-[4] gap-[3px]">
            {sortedCategories.slice(0, 4).map((category, index) => (
              <NavigationItem
                key={category.id}
                category={category}
                isOpen={openCategory === category.id}
                onToggle={() => handleToggle(category.id)}
                onClose={handleClose}
                isMobileMenu={false}
                categoryIcons={categoryIcons}
                categoryPlayful={categoryPlayful}
                entryIcons={entryIcons}
                entryPlayful={entryPlayful}
                navStyle={navStyle}
                classicColorOverride={classicColorOverride}
                colorIndex={index}
                isFirst={index === 0}
                isLast={false}
                pathname={pathname}
              />
            ))}
          </div>
          <div className="flex flex-[3] gap-[3px]">
            {sortedCategories.slice(4).map((category, index) => (
              <NavigationItem
                key={category.id}
                category={category}
                isOpen={openCategory === category.id}
                onToggle={() => handleToggle(category.id)}
                onClose={handleClose}
                isMobileMenu={false}
                categoryIcons={categoryIcons}
                categoryPlayful={categoryPlayful}
                entryIcons={entryIcons}
                entryPlayful={entryPlayful}
                navStyle={navStyle}
                classicColorOverride={classicColorOverride}
                colorIndex={index + 4}
                isFirst={false}
                isLast={index + 4 === sortedCategories.length - 1}
                pathname={pathname}
              />
            ))}
          </div>
        </div>
      ) : (
      <div className={`hidden sm:flex flex-wrap items-center bg-background ${
        `${categoryPlayful && !isSplit ? 'gap-2' : ''} ${categoryPlayful ? '' : 'space-x-1'}`
      }`}>
        {sortedCategories.map((category, index) => (
          <NavigationItem
            key={category.id}
            category={category}
            isOpen={openCategory === category.id}
            onToggle={() => handleToggle(category.id)}
            onClose={handleClose}
            isMobileMenu={false}
            categoryIcons={categoryIcons}
            categoryPlayful={categoryPlayful}
            entryIcons={entryIcons}
            entryPlayful={entryPlayful}
            navStyle={navStyle}
            classicColorOverride={classicColorOverride}
            colorIndex={index}
            isFirst={index === 0}
            isLast={index === sortedCategories.length - 1}
            pathname={pathname}
          />
        ))}
      </div>
      )}

      {/* Mobile vertical nav */}
      {mobileMenuOpen && (
        <div className="sm:hidden flex flex-col bg-background border border-gray-200 dark:border-gray-700 rounded-md shadow-lg absolute top-full left-0 right-0 z-50 max-h-[80vh] overflow-y-auto">
          {sortedCategories.map((category, index) => (
            <NavigationItem
              key={category.id}
              category={category}
              isOpen={openCategory === category.id}
              onToggle={() => handleToggle(category.id)}
              onClose={handleClose}
              isMobileMenu={true}
              categoryIcons={categoryIcons}
              categoryPlayful={categoryPlayful}
              entryIcons={entryIcons}
              entryPlayful={entryPlayful}
              navStyle={navStyle}
              classicColorOverride={classicColorOverride}
              colorIndex={index}
              isFirst={index === 0}
              isLast={index === sortedCategories.length - 1}
              pathname={pathname}
            />
          ))}
        </div>
      )}
    </nav>
  );
};
